import { Injectable } from '@angular/core';
import { Cart, CartAWB, PaymentCart, PromoCode, PromoCodeListResponse } from '@cargos/sprintpay-models';
import { PromoCodeService } from '@cargos/sprintpay-services';
import {
    BehaviorSubject,
    Observable,
    catchError,
    combineLatest,
    distinctUntilChanged,
    forkJoin,
    map,
    of,
    switchMap,
    take,
} from 'rxjs';
import { CartBillService } from './cart.service';
import { CurrentFacilityService } from './current-facility.service';

export interface AwbPromoCode {
    promoCodeId: string;
    awb: string;
    awbWithPromoCodeApplied: string;
}
export const promoCodeValue: number = 8;

interface PaymentRequestIdPromoCode {
    promoCodeId: string;
    paymentRequestId: string;
    awbWithPromoCodeApplied: string;
}

@Injectable({
    providedIn: 'root',
})
export class PromoCodesService {
    private promoCode = new BehaviorSubject<AwbPromoCode[]>([]);
    private arrayPromoCode: AwbPromoCode[] = [];
    private currentPromoCode = new BehaviorSubject<PaymentRequestIdPromoCode | null>(null);
    private isNeccesaryPromoCode: boolean = false;
    private facilityWithPromoCode = ['alliance'];
    private promoCodeListByUser = new BehaviorSubject<PromoCodeListResponse[]>([]);

    constructor(
        private cartBillService: CartBillService,
        private currentFacilityService: CurrentFacilityService,
        private promoCodeService: PromoCodeService
    ) {}

    setIdAndPromoCode(paymentRequestId: string, promoCodeId: string, awbWithPromoCodeApplied: string): void {
        this.currentPromoCode.next({
            promoCodeId,
            paymentRequestId,
            awbWithPromoCodeApplied,
        });
    }

    resetPromoCode(): void {
        this.arrayPromoCode = [];
        this.promoCode.next([]);
    }

    setPromoCodesApplied(awb: string, promoCodeId: string, awbWithPromoCodeApplied: string): void {
        const objIndex = this.arrayPromoCode.findIndex((obj) => obj.awb == awb);

        if (objIndex === -1) {
            this.arrayPromoCode.push({ awb, promoCodeId, awbWithPromoCodeApplied });
        } else {
            this.arrayPromoCode[objIndex].promoCodeId = promoCodeId;
        }

        this.promoCode.next(this.arrayPromoCode);
    }

    getAllPromoCodeApplied(): AwbPromoCode[] {
        return this.arrayPromoCode.filter((promoCode) => promoCode.promoCodeId);
    }

    findPromoCode(promoCodeId: string): boolean {
        return this.arrayPromoCode.some((promoCode) => promoCodeId === promoCode.promoCodeId);
    }

    getAllPromoCodeApplied$(): Observable<AwbPromoCode[]> {
        return this.promoCode.asObservable();
    }

    isAwbPaidWithPromoCode$(awb: string): Observable<boolean> {
        return this.getAllPromoCodeApplied$().pipe(
            map((allPromoCode: AwbPromoCode[]) => {
                return allPromoCode?.some((promoCode) => promoCode.awb === awb);
            })
        );
    }

    isAwbPaidWithPromoCode(awb: string): boolean {
        return this.arrayPromoCode.some((promoCode) => promoCode.awb === awb);
    }

    isAllAwbPaidWithPromoCode(awbsListWithPromoCode: string[]): Observable<boolean[]> {
        return forkJoin(awbsListWithPromoCode.map((awb) => this.isAwbPaidWithPromoCode$(awb).pipe(take(1))));
    }

    getAllNonReservationFee$(): Observable<number> {
        return this.getAllPromoCodeApplied$().pipe(
            switchMap(() => {
                return this.cartBillService.getPaymentRequestsGroupByAWB$().pipe(
                    switchMap((awbList: CartAWB[]) => {
                        const awbsWithPromo = awbList
                            .filter((awbItem) => awbItem.nonReservationFeeAwb)
                            .map((awbItem) => awbItem.awb);
                        if (awbsWithPromo.length) {
                            return of(awbsWithPromo);
                        }
                        return this.cartBillService.getCart$().pipe(
                            map((paymentCarts: PaymentCart[]) => {
                                return paymentCarts
                                    .filter((paymentCart) => paymentCart.nonReservationFee)
                                    .map((paymentRequest) => paymentRequest.awb || '');
                            })
                        );
                    }),
                    switchMap((paymentCarts) => {
                        return this.isAllAwbPaidWithPromoCode(paymentCarts);
                    }),
                    map((allAwbWithPromoCode: boolean[]) => {
                        return allAwbWithPromoCode.filter((response) => response === false || response === undefined);
                    }),
                    map((allAwbWithPromoCode: boolean[]) => {
                        return allAwbWithPromoCode?.length * 8;
                    })
                );
            })
        );
    }

    getNonReservationFeeApplied$(): Observable<number> {
        return this.getAllNonReservationFee$().pipe(distinctUntilChanged());
    }

    validateIfIsNeccesaryPromoCode(): Observable<boolean> {
        return this.cartBillService
            .getCart$()
            .pipe(
                map((cart: PaymentCart[]) =>
                    cart.some(
                        (payment: PaymentCart) =>
                            payment.nonReservationFee && payment.paymentType?.toLowerCase() == 'isc'
                    )
                )
            );
    }

    setIsPromoCodeNecessary(isNeccesaryPromoCode: boolean): void {
        this.isNeccesaryPromoCode = isNeccesaryPromoCode;
    }

    getIsPromoCodeNecessary(): boolean {
        return this.isNeccesaryPromoCode;
    }

    getIsNecessaryDetailInformation(): boolean {
        const facility = this.currentFacilityService.getFacilitySource();
        return this.facilityWithPromoCode.includes(facility);
    }

    getPromoCodeByAWB(awb: string): string | undefined {
        const promoCodes = this.getAllPromoCodeApplied();

        return promoCodes.find((promoCodes) => {
            return promoCodes.awb === awb;
        })?.promoCodeId;
    }

    getPromoCodeAppliedByAWB(awb: string): string | undefined {
        const promoCodes = this.getAllPromoCodeApplied();

        return promoCodes.find((promoCodes) => {
            return promoCodes.awb === awb;
        })?.awbWithPromoCodeApplied;
    }

    getPromoCodeValueByAWB$(awb: string, aboveThreshold?: boolean): Observable<number | undefined> {
        return this.cartBillService.getAWBInfoByAWB(awb, aboveThreshold).pipe(
            map((awbList: CartAWB[]) => {
                const awbWithPromo = awbList.find((item) => item.nonReservationFeeAwb !== undefined);
                return awbWithPromo?.nonReservationFeeAwb;
            }),
            switchMap((promoValue) => {
                if (promoValue !== undefined) {
                    return of(promoValue);
                }
                return this.cartBillService
                    .getCartFilterByAWB$(awb)
                    .pipe(
                        map(
                            (paymentCarts: PaymentCart[]) =>
                                paymentCarts.find((paymentCart) => paymentCart.nonReservationFee)?.nonReservationFee
                        )
                    );
            })
        );
    }

    removePromoCode(awb: string, promoCodeId: string): void {
        const objIndex = this.arrayPromoCode.findIndex((obj) => obj.promoCodeId == promoCodeId && obj.awb == awb);

        if (objIndex > -1) {
            this.arrayPromoCode.splice(objIndex, 1);
        }

        this.promoCode.next(this.arrayPromoCode);
    }

    validatePromoCode(promoCodeId: string): Observable<PromoCode> {
        return this.promoCodeService.validatePromoCode(promoCodeId);
    }

    getPromoCodesByUserRequest$(): Observable<PromoCodeListResponse[]> {
        return this.promoCodeService.getPromoCodesByUser().pipe(catchError(() => of([])));
    }

    setPromoCodeListByUser(promoCodeListByUser: PromoCodeListResponse[]): void {
        this.promoCodeListByUser.next(promoCodeListByUser);
    }

    getPromoCodesByUser$(): Observable<PromoCodeListResponse[]> {
        return this.promoCodeListByUser.asObservable().pipe(
            switchMap((promoCodesList) => {
                return this.getAllPromoCodeApplied$().pipe(
                    map((awbPromoCodes: AwbPromoCode[]) => {
                        const promoCodestoRemove = new Set(
                            awbPromoCodes.map((promoCodeUsed) => promoCodeUsed?.promoCodeId)
                        );

                        return promoCodesList.filter((promoCode) => !promoCodestoRemove.has(promoCode?.promoCodeId));
                    })
                );
            })
        );
    }

    getNonReservationFeeTotal(): Observable<number | undefined> {
        return this.cartBillService.getCartBill$().pipe(
            map((cartBill: Cart | null) => {
                let nonReservationFeeTotal = cartBill?.nonReservationFee || 0;
                if (cartBill?.threshold) {
                    nonReservationFeeTotal += cartBill?.threshold?.nonReservationFee || 0;
                }

                return nonReservationFeeTotal;
            })
        );
    }

    getNonReservationFee$(): Observable<number> {
        return combineLatest([this.getNonReservationFeeTotal(), this.getAllPromoCodeApplied$()]).pipe(
            map(([nonReservationFeeTotal, promoCodeApplied]) => {
                const totalPromoCode = promoCodeApplied.length * promoCodeValue;
                return (nonReservationFeeTotal || 0) - totalPromoCode;
            }),
            distinctUntilChanged()
        );
    }
}
